/**
 * Pure tier classifier + headline lookup for a persisted assistant turn that
 * failed (`metadata.status === "error"`, with `metadata.errorCode`). The
 * per-message error indicator renders from these: the tier picks the look
 * (quiet stop line, calm amber notice, or failure notice) and whether a
 * "Report error" affordance makes sense; the title gives the notice a
 * scannable label above the server's sanitized `errorMessage`.
 *
 * Moved here from the private `@magnus/react` transcript (plan 043 P6) so
 * the published `magnus-chat` block can render the same indicator by
 * default. Imports only a type and the provider-error copy, so it stays
 * dependency-free. The admin feedback UI intentionally keeps its own copy
 * of the classifier so the two surfaces can evolve independently.
 */

import type { MessageErrorCode } from "./messages";
import { PROVIDER_ERROR_COPY } from "./provider-errors";

export type MessageErrorTier = "error" | "warning" | "quiet";

export function classifyMessageErrorTier(
  code: MessageErrorCode | undefined
): MessageErrorTier {
  switch (code) {
    case "aborted":
      return "quiet";
    case "turn_timeout":
    case "cost_limit":
    // Step-limit hits are a quota outcome (sibling of cost/time limits),
    // not a failure — the model finished its step cleanly, we just
    // capped the agent loop. Same calm amber tier as the other limits.
    case "step_limit":
    // Rate limits are inherently transient + user-actionable (just wait a
    // moment), so we render them in the same calm amber tier as cost/time
    // limits rather than the alarming red `error` tier.
    case "provider_rate_limit":
    // An incomplete turn (stream cut off mid-tool-call) is transient and
    // fixed by retrying, so it belongs in the calm tier rather than the
    // destructive red one.
    case "incomplete_turn":
      return "warning";
    // stream_error, setup_error, workflow_start_failed, empty_response, the
    // remaining provider_* codes (unavailable / auth / bad_request / timeout
    // / model_unavailable), and unknown/undefined fall through to the
    // destructive tier — they indicate a genuine failure the user can't fix
    // by waiting.
    default:
      return "error";
  }
}

/**
 * Returns the headline shown above the (longer) `errorMessage` body in the
 * in-chat error notice. The body comes from the workflow's error messages
 * and explains the user's options; this title gives the notice a scannable
 * label.
 *
 * Returns `undefined` for codes whose tier-specific default ("Response
 * stopped" / "Something went wrong") still reads better than a custom
 * title — the indicator falls back to its tier default in that case.
 */
export function getMessageErrorTitle(
  code: MessageErrorCode | undefined
): string | undefined {
  switch (code) {
    case "cost_limit":
      return "Response stopped — cost limit reached";
    case "turn_timeout":
      return "Response stopped — time limit reached";
    case "step_limit":
      return "Response stopped — tool-call limit reached";
    case "compaction_thrashing":
    case "compaction_unactionable":
      return "Response stopped — context too large";
    case "incomplete_turn":
      return "Response was cut off";
    case "provider_rate_limit":
    case "provider_unavailable":
    case "provider_timeout":
    case "provider_auth_error":
    case "provider_bad_request":
    case "model_unavailable":
      return PROVIDER_ERROR_COPY[code].title;
    default:
      return undefined;
  }
}
